import { createSlice } from '@reduxjs/toolkit';

import type { AutenticacaoPublicaState } from '../types/autenticacaoPublica.types';

import { consultarDocumento, downloadDocumento, verificarArquivo } from './actions';

interface RejectPayload {
  message: string;
  retryAfterSeconds?: number;
}

const initialState: AutenticacaoPublicaState = {
  consultaResult: null,
  verificacaoResult: null,
  downloadStatus: 'IDLE',
  isLoadingConsulta: false,
  isLoadingVerificacao: false,
  isLoadingDownload: false,
  errorConsulta: null,
  errorVerificacao: null,
  errorDownload: null,
  rateLimitRetryAfter: null,
};

const autenticacaoPublicaSlice = createSlice({
  name: 'autenticacaoPublica',
  initialState,
  reducers: {
    resetConsulta: (state) => {
      state.consultaResult = null;
      state.errorConsulta = null;
      state.rateLimitRetryAfter = null;
    },
    resetVerificacao: (state) => {
      state.verificacaoResult = null;
      state.errorVerificacao = null;
    },
    /**
     * O documento não ficou pronto dentro do teto do polling. É um desfecho anunciado, não um
     * reset: antes a expiração dava `resetDownload()` e a tela voltava ao estado inicial sem
     * dizer nada — o usuário via o botão de novo e nenhuma explicação.
     */
    expireDownload: (state) => {
      state.isLoadingDownload = false;
      state.downloadStatus = 'ERROR';
      state.errorDownload =
        'O documento ainda está sendo preparado. Tente novamente em alguns instantes.';
    },
    resetAll: () => initialState,
    clearRateLimit: (state) => {
      state.rateLimitRetryAfter = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(consultarDocumento.pending, (state) => {
        state.isLoadingConsulta = true;
        state.errorConsulta = null;
        state.consultaResult = null;
        state.rateLimitRetryAfter = null;
      })
      .addCase(consultarDocumento.fulfilled, (state, action) => {
        state.isLoadingConsulta = false;
        state.consultaResult = action.payload;
      })
      .addCase(consultarDocumento.rejected, (state, action) => {
        state.isLoadingConsulta = false;
        const payload = action.payload as RejectPayload | undefined;
        state.errorConsulta = payload?.message ?? 'Erro ao consultar documento';
        if (payload?.retryAfterSeconds) {
          state.rateLimitRetryAfter = payload.retryAfterSeconds;
        }
      })

      .addCase(verificarArquivo.pending, (state) => {
        state.isLoadingVerificacao = true;
        state.errorVerificacao = null;
        state.verificacaoResult = null;
        state.rateLimitRetryAfter = null;
      })
      .addCase(verificarArquivo.fulfilled, (state, action) => {
        state.isLoadingVerificacao = false;
        state.verificacaoResult = action.payload;
      })
      .addCase(verificarArquivo.rejected, (state, action) => {
        state.isLoadingVerificacao = false;
        const payload = action.payload as RejectPayload | undefined;
        state.errorVerificacao = payload?.message ?? 'Erro ao verificar arquivo';
        if (payload?.retryAfterSeconds) {
          state.rateLimitRetryAfter = payload.retryAfterSeconds;
        }
      })

      // O `pending` NÃO zera `downloadStatus`. Zerar era o motor do item 13: cada repergunta do
      // polling derrubava o status para `IDLE`, o efeito que mantém o timer remontava e
      // recarimbava o prazo, e o teto de 60s nunca chegava. Quem diz "requisição em voo" é
      // `isLoadingDownload`; `downloadStatus` diz em que ponto o download está.
      .addCase(downloadDocumento.pending, (state) => {
        state.isLoadingDownload = true;
        state.errorDownload = null;
      })
      .addCase(downloadDocumento.fulfilled, (state, action) => {
        state.isLoadingDownload = false;
        state.downloadStatus = action.payload.status === 'GENERATING' ? 'GENERATING' : 'DOWNLOADED';
      })
      .addCase(downloadDocumento.rejected, (state, action) => {
        state.isLoadingDownload = false;
        // Abortada por quem encerrou a sessão (expiração ou saída da tela): quem abortou já
        // escreveu o desfecho. Tratá-la como falha trocaria "expirou" por uma mensagem genérica
        // de AbortError. A trava acima é solta de qualquer jeito — presa, a `condition` do thunk
        // bloquearia todo download seguinte.
        if (action.meta.aborted) return;
        state.downloadStatus = 'ERROR';
        const payload = action.payload as RejectPayload | undefined;
        state.errorDownload = payload?.message ?? 'Erro ao baixar documento';
        if (payload?.retryAfterSeconds) {
          state.rateLimitRetryAfter = payload.retryAfterSeconds;
        }
      });
  },
});

export const { expireDownload, resetAll, clearRateLimit } = autenticacaoPublicaSlice.actions;
export default autenticacaoPublicaSlice.reducer;
